import { createContext, useState, useEffect } from 'react'
import { useReadLocalStorage, useDarkMode } from 'usehooks-ts'

// Helper function to safely check if we're in the browser
const isBrowser = typeof window !== 'undefined'

// Helper function to get system theme preference safely
const getSystemTheme = () => {
  if (!isBrowser) return 'light' // Default to light on server
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export const ThemeContext = createContext({
  currentTheme: 'light', // Use a safe default for SSR
})

interface ThemeProviderProps {
  contentfulDarkModeSet?: boolean
  children: React.ReactNode
}

export const ThemeProvider = ({ contentfulDarkModeSet, children }: ThemeProviderProps) => {
  // Initialize with a safe default, will be updated on client
  const [currentTheme, setCurrentTheme] = useState('light')
  const [isClient, setIsClient] = useState(false)

  const theme = useReadLocalStorage('theme')
  const enableDarkMode = useReadLocalStorage('bwDarkMode')

  // Set client flag after hydration
  useEffect(() => {
    setIsClient(true)
    // Set initial theme based on system preference after client hydration
    if (theme === null) {
      const systemTheme = getSystemTheme()
      setCurrentTheme(systemTheme)
    }
  }, [])

  useEffect(() => {
    // Only run DOM manipulation on the client
    if (!isClient) return

    const doc = document.documentElement

    // temporary dark mode for developers page until we go for full dark mode support
    if (contentfulDarkModeSet) {
      doc.classList.add('dark')
    } else {
      doc.classList.remove('dark')
    }

    // if we haven't chosen a theme yet, use the system preference
    if (theme === null) {
      const systemTheme = getSystemTheme()
      if (systemTheme === 'dark') {
        doc.classList.add('dark')
      } else {
        doc.classList.remove('dark')
      }
      setCurrentTheme(systemTheme)
    }
    // if we have chosen a specific theme
    else if (theme === 'light') {
      setCurrentTheme('light')
      doc.classList.remove('dark')
    }
    else if (theme === 'dark') {
      setCurrentTheme('dark')
      doc.classList.add('dark')
    }
  }, [theme, isClient, contentfulDarkModeSet])

  return (
    <ThemeContext.Provider value={{ currentTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}
